import { useAuth, useUser } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import { usePlausible } from "next-plausible";
import posthog from "posthog-js";
import { type PropsWithChildren, useEffect } from "react";
import { mutate as globalMutate } from "swr";

import {
	useAccess,
	useLazyIntercom,
	useSelectedCertification,
	useSelectedProfessions,
} from "~/hooks";

const TrackingProvider = ({ children }: PropsWithChildren<{}>) => {
	const track = usePlausible();
	const router = useRouter();
	const { userId, isLoaded, actor } = useAuth();
	const { user } = useUser();

	const {
		isEmployee,
		isMember,
		isFormerMember,
		isLoadingAccess,
		accessLevel,
		status,
	} = useAccess();
	const { selectedCertification, isLoadingSelectedCertification } =
		useSelectedCertification();
	const { selectedProfessions, isLoadingSelectedProfessions } =
		useSelectedProfessions();

	useEffect(() => {
		if (isLoadingAccess || !isLoaded) return;

		if (!userId) {
			track("Visitor (Anonymous)");
		} else {
			track("User (Logged In)", {
				props: { accessLevel },
			});
		}
	}, [
		accessLevel,
		isEmployee,
		isFormerMember,
		isLoaded,
		isLoadingAccess,
		isMember,
		track,
		userId,
	]);

	useEffect(() => {
		globalMutate(() => true);
	}, [userId]);

	const { update, shutdown } = useLazyIntercom();

	useEffect(() => {
		if (
			isLoadingSelectedCertification ||
			isLoadingSelectedProfessions ||
			isLoadingAccess ||
			!isLoaded
		)
			return;

		if (user) {
			const email = user.emailAddresses[0]?.emailAddress;

			posthog.identify(user.id, {
				email: email ?? null,
				$email: email ?? null,
				subscription_status: status ?? null,
				access_level: accessLevel,
				primary_profession_id:
					selectedProfessions?.primaryProfession?._id ?? null,
				selected_certification_id: selectedCertification?._id ?? null,
			});

			posthog.reloadFeatureFlags();

			if (actor) {
				shutdown();
			} else {
				update({
					email: email,
					createdAt: user.createdAt?.toISOString(),
					name: user.firstName + " " + user.lastName,
					userId: user.id,

					customAttributes: {
						path: router?.asPath,
						subscription_status: status,
						primary_profession_id: selectedProfessions?.primaryProfession?._id,
						selected_certification_id: selectedCertification?._id,
					},
				});
			}
		}
	}, [
		actor,
		accessLevel,
		isLoaded,
		isLoadingAccess,
		isLoadingSelectedCertification,
		isLoadingSelectedProfessions,
		router?.asPath,
		selectedCertification?._id,
		selectedProfessions?.primaryProfession?._id,
		shutdown,
		status,
		update,
		user,
	]);

	return <>{children}</>;
};

export default TrackingProvider;
